import { useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import FiscalYear from "models/fiscal-year"
import FiscalCalendar from "utils/fiscal-calendar"
import { setVisibleFiscalYears } from "slices/fiscal-years/reducer"
import { getVisibleFiscalYears } from "slices/fiscal-years/thunk"

export interface FiscalYearOption {
    label: string
    value: string
}

interface UseFiscalYearOptionsResult {
    /** Visible fiscal years, newest first. */
    fiscalYears: FiscalYear[]
    /** Dropdown options, newest first. Values are strings ("2026"). */
    options: FiscalYearOption[]
    /** The current fiscal year if it is visible, otherwise the newest visible year. */
    defaultYear: number
    isLoading: boolean
}

// Shared across every component that mounts at the same time, so the list is fetched once.
let inFlight: Promise<FiscalYear[] | null> | null = null

/**
 * Fiscal year dropdown options from the Admin → Fiscal Years catalog (hidden years excluded).
 * @param keepYear A year to always include even when hidden, e.g. the value of a record being edited.
 */
const useFiscalYearOptions = (keepYear?: number | string | null): UseFiscalYearOptionsResult => {
    const dispatch: any = useDispatch()
    const loaded: FiscalYear[] | null = useSelector((state: any) => state.FiscalYears.fiscalYears)

    useEffect(() => {
        if (loaded) return
        if (!inFlight) {
            inFlight = getVisibleFiscalYears().finally(() => { inFlight = null })
        }
        inFlight.then(res => dispatch(setVisibleFiscalYears(res ?? [])))
    }, [loaded])

    const fiscalYears = useMemo(() => loaded ?? [], [loaded])

    const options = useMemo(() => {
        const years = fiscalYears.map(f => f.id)
        const keep = keepYear !== undefined && keepYear !== null && keepYear !== '' ? Number(keepYear) : NaN
        if (Number.isInteger(keep) && !years.includes(keep)) years.push(keep)
        return years
            .sort((a, b) => b - a)
            .map(y => ({ label: String(y), value: String(y) }))
    }, [fiscalYears, keepYear])

    const defaultYear = useMemo(() => {
        const current = FiscalCalendar.lastMonthFiscalYear()
        if (fiscalYears.length === 0 || fiscalYears.some(f => f.id === current)) return current
        return fiscalYears[0].id
    }, [fiscalYears])

    return { fiscalYears, options, defaultYear, isLoading: loaded === null }
}

export default useFiscalYearOptions
